import React, { useState, useEffect } from 'react';
import { getStockStatusColor } from '../utils/colorData';
import { removeFromCart, updateQuantity } from '../utils/cart';
import CheckoutForm from './CheckoutForm';
import OrderConfirmation from './OrderConfirmation';

const supabaseStorageUrl = "https://xsuruuynxrxrarvalzjb.supabase.co/storage/v1/object/public/images/";

const CartDrawer = ({ isOpen, onClose, cartItems, onUpdateQuantity, onRemoveItem, onCheckoutComplete, onShowHistory }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [cartHistory, setCartHistory] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Cargar historial cuando se abre
  useEffect(() => {
    if (isOpen) {
      const history = JSON.parse(localStorage.getItem('cartHistory') || '[]');
      setCartHistory(history);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Duración de la transición
  };

  const calculateTotal = () => {
    return Math.round(cartItems.reduce((total, item) => total + (item.pricears * item.quantity), 0));
  };

  const handleCheckout = () => {
    setShowCheckout(true);
  };

  const handleCheckoutComplete = (order) => {
    setShowCheckout(false);
    setCompletedOrder(order);
    if (onCheckoutComplete) {
      onCheckoutComplete(order);
    }
  };

  const handleCheckoutCancel = () => {
    setShowCheckout(false);
  };

  const handleOrderConfirmationClose = () => {
    setCompletedOrder(null);
    onClose();
  };

  // Función para obtener el estado de stock del color seleccionado
  const getColorStockStatus = (productName, selectedColor) => {
    const { getProductColors } = require('../utils/colorData');
    const colors = getProductColors(productName);
    const color = colors.find(c => c.name === selectedColor);
    return color ? color.stock : null;
  };

  return (
    <div className={`fixed inset-0 z-50 ${isOpen ? 'block' : 'hidden'}`}>
      <div 
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
        }`} 
        onClick={handleClose}
      ></div>
      <div 
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl rounded-l-xl transform transition-transform duration-300 ease-in-out ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6 h-full flex flex-col">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-900">Tu Carrito</h2>
            <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 p-1 rounded-full transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="flex-grow overflow-y-auto pr-2">
            {cartItems.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Tu carrito está vacío, ¡añade algo!</p>
            ) : (
              <div className="space-y-4">
                {cartItems.map(item => {
                  const stockStatus = item.selectedColor ? getColorStockStatus(item.name, item.selectedColor) : null;
                  // Usar el itemKey persistente
                  const itemKey = item.itemKey;
                  
                  return (
                    <div key={itemKey} className="flex items-center gap-4 bg-gray-50 p-3 rounded-lg shadow-sm">
                      <div className="w-20 h-20 bg-white rounded-md flex items-center justify-center overflow-hidden">
                        {item.image ? (
                          <img 
                            src={item.image ? (supabaseStorageUrl + item.image) : (supabaseStorageUrl + 'blank.jpg')}
                            alt={item.name}
                            className="w-16 h-16 object-contain"
                            onError={e => { e.target.onerror = null; e.target.src = process.env.PUBLIC_URL + '/images/products/blank.jpg'; }}
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center text-gray-400">
                            <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs">Sin imagen</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-grow">
                        <h3 className="font-medium text-gray-900">{item.name}</h3>
                        {item.selectedColor && (
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm text-gray-600">
                              Color: <span className="font-medium">{item.selectedColor}</span>
                            </p>
                            {stockStatus && (
                              <span className={`text-xs px-2 py-1 rounded-full ${getStockStatusColor(stockStatus)}`}>
                                {stockStatus}
                              </span>
                            )}
                          </div>
                        )}
                        <p className="text-gray-600 text-sm">
                          ${item.pricears.toLocaleString('es-AR')}
                          {typeof item.price !== 'undefined' && item.price !== null && (
                            <span className="block text-green-600 text-xs font-semibold mt-1">
                              USD ${item.price.toLocaleString('en-US')}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => onUpdateQuantity(itemKey, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-md text-gray-700 hover:bg-gray-100 transition-colors transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
                        >
                          <span>-</span>
                        </button>
                        <span className="font-medium text-gray-800">{item.quantity}</span>
                        <button 
                          onClick={() => onUpdateQuantity(itemKey, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-md text-gray-700 hover:bg-gray-100 transition-colors transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
                        >
                          <span>+</span>
                        </button>
                      </div>
                      <button 
                        onClick={() => onRemoveItem(itemKey)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-full transition-colors transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-opacity-50"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="border-t border-gray-100 pt-6 mt-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-medium text-gray-700">Total</span>
              <span className="text-2xl font-bold text-gray-900">
                ${calculateTotal().toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                {cartItems.some(item => typeof item.price !== 'undefined' && item.price !== null) && (
                  <span className="block text-green-600 text-xs font-semibold mt-1 text-right">
                    USD ${cartItems.reduce((acc, item) => acc + (Number(item.price) * item.quantity), 0).toLocaleString('en-US')}
                  </span>
                )}
              </span>
            </div>
            <button 
              onClick={handleCheckout}
              disabled={cartItems.length === 0}
              className="w-full py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-lg font-medium shadow-md transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-black focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Finalizar Compra
            </button>
            <button
              onClick={() => {
                console.log('Abriendo historial desde CartDrawer');
                setShowHistory(true);
              }}
              className="w-full py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold mt-3"
            >
              Ver historial de compras
            </button>

          </div>
        </div>
      </div>
      
      {/* Formulario de checkout */}
      {showCheckout && (
        <CheckoutForm
          cartItems={cartItems}
          total={calculateTotal()}
          onComplete={handleCheckoutComplete}
          onCancel={handleCheckoutCancel}
        />
      )}
      
      {/* Confirmación de orden */}
      {completedOrder && (
        <OrderConfirmation
          order={completedOrder}
          onClose={handleOrderConfirmationClose}
          onClearCart={() => {
            console.log('Limpiando carrito desde OrderConfirmation');
            
            // Guardar en historial antes de limpiar
            const currentItems = [...cartItems];
            const total = currentItems.reduce((acc, item) => acc + (item.pricears * item.quantity), 0);
            
            const newEntry = {
              date: new Date().toLocaleString(),
              items: currentItems,
              total: total
            };
            
            // Obtener historial actual y agregar nueva entrada
            const currentHistory = JSON.parse(localStorage.getItem('cartHistory') || '[]');
            const updatedHistory = [newEntry, ...currentHistory];
            localStorage.setItem('cartHistory', JSON.stringify(updatedHistory));
            
            console.log('Guardado en historial:', newEntry);
            
            // Limpiar carrito
            localStorage.removeItem('cartItems');
            window.location.reload();
          }}
        />
      )}

      {/* Modal de historial */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            <button onClick={() => setShowHistory(false)} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-1 rounded-full transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-2xl font-bold mb-4">Historial de Compras</h2>
            {cartHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No hay historial de compras.</p>
            ) : (
              <div className="space-y-6">
                {cartHistory.map((entry, idx) => (
                  <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">{entry.date}</span>
                      <span className="text-lg font-bold text-gray-900">Total: ${entry.total.toLocaleString('es-AR')}</span>
                    </div>
                    <ul className="divide-y divide-gray-200">
                      {entry.items.map((item, i) => (
                        <li key={i} className="py-2 flex items-center gap-3">
                          <img src={item.image ? (supabaseStorageUrl + item.image) : (supabaseStorageUrl + 'blank.jpg')} alt={item.name} className="w-10 h-10 object-contain rounded" />
                          <span className="font-medium text-gray-900">{item.name}</span>
                          {item.selectedColor && <span className="text-xs px-2 py-1 rounded bg-gray-200 ml-2">{item.selectedColor}</span>}
                          <span className="ml-auto text-gray-700">x{item.quantity}</span>
                          <span className="ml-4 text-gray-700">${item.pricears.toLocaleString('es-AR')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <button 
              onClick={() => {
                setCartHistory([]);
                localStorage.removeItem('cartHistory');
                setShowHistory(false);
              }} 
              className="mt-6 w-full py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold"
            >
              Limpiar Historial
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartDrawer;